import React, { ReactNode, useCallback, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { Icon } from '../../ui';
import { Portal } from '../Portal';
import {
  type ModalSizeKey,
  StyledCloseButton,
  StyledContent,
  StyledContentInnerWrapper,
  StyledWrapper,
} from './Modal.style';

export interface ModalProps {
  size?: ModalSizeKey;
  open?: boolean;
  portalTarget?: Element;
  children?: ReactNode;
  variant?: 'custom' | 'default';
  onOverlayClick?: () => void;
  onClose?: () => void;
  hasInnerPadding?: boolean;
}

export function Modal({
  size,
  open = true,
  portalTarget,
  children,
  onOverlayClick,
  variant = 'default',
  onClose,
  hasInnerPadding = true,
}: ModalProps) {
  const theme = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node) &&
        !(event.target as Element)?.closest('.ant-select-dropdown') &&
        !(event.target as Element)?.closest('.ant-dropdown')
      ) {
        onOverlayClick?.();
      }
    },
    [onOverlayClick]
  );
  const isMobile = useMediaQuery({ maxWidth: theme.Screen.Size.Small });

  return (
    <Portal target={portalTarget}>
      <StyledWrapper onClick={handleClickOutside} style={{ display: open ? 'inherit' : 'none' }}>
        <StyledContent ref={contentRef} $size={size} $isMobile={isMobile} $variant={variant}>
          {onClose && (
            <StyledCloseButton onClick={onClose}>
              <Icon name="cross" size={'Medium'} color={theme.Color.Neutrals.Paper} />
            </StyledCloseButton>
          )}
          <StyledContentInnerWrapper $innerPadding={hasInnerPadding}>
            {children}
          </StyledContentInnerWrapper>
        </StyledContent>
      </StyledWrapper>
    </Portal>
  );
}
